import { Box, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { Lightbulb02Icon } from '@/components/icons/Lightbulb02Icon';
import { OnboardingTitle } from '@/components/modals/OnboardingModal/OnboardingModal.components';
import { COLORS } from '@/styles/tokens/colors';

import {
  ClientOnboardingDeliverableConfig,
  ClientOnboardingDeliverableVariant,
} from '../../types';
import { DELIVERABLE_SIDEBAR_CONFIG } from '../deliverableConfig';

interface UseClientOnboardingDeliverableModalSidebarContentProps {
  variant: ClientOnboardingDeliverableVariant | null;
}

interface ClientOnboardingDeliverableModalSidebarContent {
  title: ReactNode;
  description: ReactNode;
  sidebarContent: ReactNode;
}

/**
 * useClientOnboardingDeliverableModalSidebarContent is a hook that returns the
 * sidebar content for the ClientOnboardingDeliverableModal based on the
 * variant provided.
 *
 * @param variant
 */
export function useClientOnboardingDeliverableModalSidebarContent({
  variant,
}: UseClientOnboardingDeliverableModalSidebarContentProps): ClientOnboardingDeliverableModalSidebarContent | null {
  const content = useMemo(() => {
    if (!variant) {
      return null;
    }

    const config = DELIVERABLE_SIDEBAR_CONFIG[variant];
    if (!config) {
      throw new Error(
        `unknown client onboarding deliverable variant: ${variant}`
      );
    }

    return getSidebarContent(config);
  }, [variant]);

  return content;
}

export function getSidebarContent({
  title,
  description,
  illustration,
  calloutText,
}: ClientOnboardingDeliverableConfig) {
  return {
    title: <OnboardingTitle>{title}</OnboardingTitle>,
    description: <Typography variant="h2light">{description}</Typography>,
    sidebarContent: (
      <Stack direction="column" gap={2} px={2}>
        <Box width="100%" display="flex" justifyContent="center">
          {illustration}
        </Box>
        {calloutText && (
          <Stack
            direction="row"
            p={2}
            gap={1}
            borderRadius={1}
            bgcolor={COLORS.NAVY[800]}
            alignItems="center"
          >
            <Lightbulb02Icon color={COLORS.FUNCTIONAL.WARNING.DEFAULT} />
            <Typography variant="body-xs" color="white">
              {calloutText}
            </Typography>
          </Stack>
        )}
      </Stack>
    ),
  };
}
